/**
 * canadianTax.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Canadian Tax Module — Rate Engine & Calculator
 *
 * Provides CRA-accurate GST/HST/PST/QST rate tables and a pure calculation
 * function that derives the correct tax breakdown based on:
 *   - The store's registered province (nexus)
 *   - The customer's shipping province
 *
 * Tax rules follow the CRA "place of supply" rules as of 2024:
 *   - HST provinces: tax is fully collected by supplier at HST rate.
 *   - Non-HST provinces (BC, MB, SK, QC): GST collected + provincial tax applies.
 *   - Alberta and territories: GST only (5%).
 *   - Quebec: GST (5%) + QST (9.975%) always, regardless of store location.
 *
 * USAGE:
 *   import { calculateCanadianTax, PROVINCE_TAX_RATES, TAX_MODULE_DEFAULTS } from './canadianTax';
 *   const result = calculateCanadianTax(subtotal, customerProvince, taxConfig);
 *
 * RETURNS:
 *   { gst, pst, hst, qst, total, breakdown, effectiveRate, taxableAmount }
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ─── Rate Table ───────────────────────────────────────────────────────────────
// All rates expressed as decimals (e.g. 0.05 = 5%)
// taxType: 'hst' | 'gst+pst' | 'gst+qst' | 'gst'
export const PROVINCE_TAX_RATES = {
    // HST Provinces (combined federal+provincial, collected as one)
    ON: { name: 'Ontario',                       taxType: 'hst',      hst: 0.13,  gst: 0,    pst: 0,       qst: 0       },
    NB: { name: 'New Brunswick',                  taxType: 'hst',      hst: 0.15,  gst: 0,    pst: 0,       qst: 0       },
    NS: { name: 'Nova Scotia',                    taxType: 'hst',      hst: 0.15,  gst: 0,    pst: 0,       qst: 0       },
    PE: { name: 'Prince Edward Island',           taxType: 'hst',      hst: 0.15,  gst: 0,    pst: 0,       qst: 0       },
    NL: { name: 'Newfoundland and Labrador',      taxType: 'hst',      hst: 0.15,  gst: 0,    pst: 0,       qst: 0       },

    // GST + PST Provinces (separate taxes collected)
    BC: { name: 'British Columbia',               taxType: 'gst+pst',  hst: 0,     gst: 0.05, pst: 0.07,    qst: 0       },
    MB: { name: 'Manitoba',                       taxType: 'gst+pst',  hst: 0,     gst: 0.05, pst: 0.07,    qst: 0       },
    SK: { name: 'Saskatchewan',                   taxType: 'gst+pst',  hst: 0,     gst: 0.05, pst: 0.06,    qst: 0       },

    // Quebec — GST + QST (QST is calculated on the GST-inclusive amount)
    QC: { name: 'Quebec',                         taxType: 'gst+qst',  hst: 0,     gst: 0.05, pst: 0,       qst: 0.09975 },

    // GST Only Provinces/Territories
    AB: { name: 'Alberta',                        taxType: 'gst',      hst: 0,     gst: 0.05, pst: 0,       qst: 0       },
    NT: { name: 'Northwest Territories',          taxType: 'gst',      hst: 0,     gst: 0.05, pst: 0,       qst: 0       },
    NU: { name: 'Nunavut',                        taxType: 'gst',      hst: 0,     gst: 0.05, pst: 0,       qst: 0       },
    YT: { name: 'Yukon',                          taxType: 'gst',      hst: 0,     gst: 0.05, pst: 0,       qst: 0       },
};

// ─── Province Name to Code Lookup ─────────────────────────────────────────────
const PROVINCE_NAME_MAP = {
    'ALBERTA': 'AB',
    'BRITISH COLUMBIA': 'BC',
    'MANITOBA': 'MB',
    'NEW BRUNSWICK': 'NB',
    'NEWFOUNDLAND AND LABRADOR': 'NL',
    'NEWFOUNDLAND': 'NL',
    'NORTHWEST TERRITORIES': 'NT',
    'NOVA SCOTIA': 'NS',
    'NUNAVUT': 'NU',
    'ONTARIO': 'ON',
    'PRINCE EDWARD ISLAND': 'PE',
    'QUEBEC': 'QC',
    'QUÉBEC': 'QC',
    'SASKATCHEWAN': 'SK',
    'YUKON': 'YT',
};

// ─── Default Module Configuration ─────────────────────────────────────────────
export const TAX_MODULE_DEFAULTS = {
    enabled: true,                  // Master switch — active by default for Canadian stores
    storeProvince: 'ON',            // Registered business province (nexus)
    applyToAllProvinces: true,      // Collect tax for all Canadian customers
    applyOnlyInStoreProvince: false,// Only collect when customer is in same province
    showTaxBreakdown: true,         // Show line-by-line GST/PST/QST in cart summary
    taxIncludedInPrice: false,      // If true, prices are treated as tax-inclusive
    roundingMode: 'standard',       // 'standard' | 'up' | 'down'
    customRates: {},                // province code → override rate object (advanced)
    exemptCategories: [],           // Category names that are tax-exempt
    registrationNumber: '',         // Display-only GST/HST registration number
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const applyRounding = (value, mode) => {
    if (mode === 'up')   return Math.ceil(value * 100) / 100;
    if (mode === 'down') return Math.floor(value * 100) / 100;
    return round2(value);
};

/**
 * Resolves the effective rate object for a given province,
 * respecting any custom rate overrides stored in taxConfig.customRates.
 */
const getEffectiveRates = (provinceCode, taxConfig) => {
    const base = PROVINCE_TAX_RATES[provinceCode] || PROVINCE_TAX_RATES['AB']; // fallback GST only
    const override = (taxConfig?.customRates || {})[provinceCode];
    if (!override) return base;
    return { ...base, ...override };
};

// ─── Main Calculator ──────────────────────────────────────────────────────────
/**
 * calculateCanadianTax
 *
 * @param {number}  subtotal        — Pre-tax cart subtotal
 * @param {string}  customerProvince — Two-letter province code or full province name
 * @param {object}  taxConfig        — Module config (from modules.canadianTax)
 * @returns {{ gst, pst, hst, qst, total, effectiveRate, breakdown, taxableAmount, appliesTax }}
 */
export const calculateCanadianTax = (subtotal, customerProvince, taxConfig = {}) => {
    const cfg = { ...TAX_MODULE_DEFAULTS, ...taxConfig };

    const ZERO = { gst: 0, pst: 0, hst: 0, qst: 0, total: 0, effectiveRate: 0, taxableAmount: subtotal, breakdown: [], appliesTax: false };

    // Guard: module explicitly disabled
    if (cfg.enabled === false) return ZERO;

    // Guard: resolve customer province code
    const rawProv = (customerProvince || '').trim().toUpperCase();
    const provCode = PROVINCE_TAX_RATES[rawProv] ? rawProv : (PROVINCE_NAME_MAP[rawProv] || '');
    if (!provCode || !PROVINCE_TAX_RATES[provCode]) return ZERO;

    // Nexus / apply logic
    if (cfg.applyOnlyInStoreProvince && provCode !== cfg.storeProvince) return ZERO;

    const roundFn = (v) => applyRounding(v, cfg.roundingMode);
    const rates = getEffectiveRates(provCode, cfg);

    let taxableAmount = subtotal;
    // If prices are tax-inclusive, back-calculate the pre-tax base
    if (cfg.taxIncludedInPrice) {
        const combinedRate = rates.hst + rates.gst + rates.pst + rates.qst;
        taxableAmount = round2(subtotal / (1 + combinedRate));
    }

    let gst = 0, pst = 0, hst = 0, qst = 0;
    const breakdown = [];

    switch (rates.taxType) {
        case 'hst':
            hst = roundFn(taxableAmount * rates.hst);
            breakdown.push({ label: `HST (${(rates.hst * 100).toFixed(0)}%)`, amount: hst });
            break;

        case 'gst+pst':
            gst = roundFn(taxableAmount * rates.gst);
            pst = roundFn(taxableAmount * rates.pst);
            breakdown.push({ label: `GST (${(rates.gst * 100).toFixed(0)}%)`, amount: gst });
            breakdown.push({ label: `PST (${(rates.pst * 100).toFixed(0)}%)`, amount: pst });
            break;

        case 'gst+qst':
            // QST is applied to the GST-inclusive amount (TVQ rule)
            gst = roundFn(taxableAmount * rates.gst);
            qst = roundFn((taxableAmount + gst) * rates.qst);
            breakdown.push({ label: `GST (${(rates.gst * 100).toFixed(0)}%)`, amount: gst });
            breakdown.push({ label: `QST (${(rates.qst * 100 * 100 / 100).toFixed(3)}%)`, amount: qst });
            break;

        case 'gst':
        default:
            gst = roundFn(taxableAmount * rates.gst);
            breakdown.push({ label: `GST (${(rates.gst * 100).toFixed(0)}%)`, amount: gst });
            break;
    }

    const total = round2(gst + pst + hst + qst);
    const effectiveRate = taxableAmount > 0 ? round2((total / taxableAmount) * 100) : 0;

    return {
        gst,
        pst,
        hst,
        qst,
        total,
        effectiveRate,
        taxableAmount,
        breakdown,
        appliesTax: total > 0,
        provinceName: rates.name,
        taxType: rates.taxType,
    };
};

/**
 * getProvinceDisplayName — Friendly name for a province code.
 */
export const getProvinceDisplayName = (code) => {
    return PROVINCE_TAX_RATES[code]?.name || code;
};

/**
 * formatTaxSummary — Returns a one-line string for order receipts.
 * e.g. "HST 13% — $12.48" or "GST 5% + PST 7% — $14.97"
 */
export const formatTaxSummary = (taxResult) => {
    if (!taxResult?.appliesTax) return 'Tax: $0.00';
    return taxResult.breakdown.map(b => `${b.label}: $${b.amount.toFixed(2)}`).join(' + ');
};
